/**
 * Admin · AI providers (007 relay-api) — SUPER-ADMIN ONLY. Full settings
 * management: active provider selection, API key configuration, model IDs,
 * rate limits, and usage stats. The server NEVER serializes secret key values
 * back — only `keys_configured` booleans — so nothing sensitive renders.
 */
import { useEffect, useState } from "react";
import { Accordion } from "../ui/Accordion.js";
import { ActionBar } from "../ui/ActionBar.js";
import { EmptyState } from "../ui/EmptyState.js";
import { useServices } from "../app/context.js";
import { Icon } from "../ui/Icon.js";
import { Tabs } from "../ui/Tabs.js";
import { InfoHint } from "../ui/Tooltip.js";
import { PROVIDERS_HELP, valueHelpText } from "../ui/valueHelp.js";
import { UsagePanel } from "./UsagePanel.js";
import { PRODUCT_HEIGHT_DEFAULT, ProductHeightControl } from "../wizard/ProductHeightControl.js";
import { useSessionStore } from "../stores/session.js";
import { cacheEpoch, readCacheEntry, writeCache } from "../stores/cache.js";
import { StaleNotice } from "./StaleNotice.js";
// WS-B B-3: the provider key is a WIRE value — it stays raw in every `key`, `value`,
// `data-testid` and payload; only what a person reads goes through `enumLabel`. This tab
// showed `Higgsfield`, `higgsfield` and `Gpt` within ~350px of each other (AUD-6 IMP-5).
import { enumLabel } from "../format/labels.js";
import {
  CASE_JOB_MODEL_DEFAULTS,
  CASE_WALLET_LABELS,
  DEFAULT_HF_FRAME,
  DEFAULT_HIGGSFIELD_MODEL,
  HF_QUALITIES,
  HF_RESOLUTIONS,
  HIGGSFIELD_GROUP_LABELS,
  TEXT_TO_IMAGE_NOTE,
  modelsFor,
  selectableModelsFor,
  type CaseWallet,
  type HfFrame,
} from "../generate/higgsfieldModels.js";
import { HiggsfieldCompanyConnection, HiggsfieldCompanyReadOnly } from "./HiggsfieldCompanyConnection.js";
import type { Staleness } from "../stores/useCachedResource.js";
import type { AiSettings, AiUsage, CaseJobStage, GenerationJobExecutor } from "@genz/platform";

/** What the server stores for the case-job stage models: one pair per wallet. */
type CaseJobModels = Partial<Record<GenerationJobExecutor | "subscription", Partial<Record<CaseJobStage, string>>>>;

const AI_SETTINGS_CACHE = "ai.settings";

/** WS-HF — where the operator manages the account this provider bills against. */
const HIGGSFIELD_CONSOLE_URL = "https://console.higgsfield.ai";

/**
 * WS-HF — US dollars per credit, for pricing a vendor row that carries credits only.
 *
 * The server's price is NOT a setting: it is `GENZ_AI_HIGGSFIELD_USD_PER_CREDIT`
 * (`cloud/app/ai_relay/usage.py`, default 0.04), read per call. An editable field here wrote a
 * value the server silently dropped, which is a price shown as saved and never used.
 */
export const DEFAULT_USD_PER_CREDIT = 0.04;

/** The two case-job stages, in the order they run. */
const CASE_STAGES = [
  { key: "clear", label: "Stage 1 · Clear case" },
  { key: "magenta", label: "Stage 2 · Print guide" },
] as const;

/** The three wallets, in the order the rows read. WS-HF shared login added the company
 *  subscription, whose pair the cloud worker runs over MCP. */
const CASE_EXECUTORS: { key: CaseWallet; label: string }[] = [
  { key: "cloud", label: CASE_WALLET_LABELS.cloud },
  { key: "device", label: CASE_WALLET_LABELS.device },
  { key: "subscription", label: CASE_WALLET_LABELS.subscription },
];

/**
 * WS-HF shared login — which wallet every designer's CLOUD case job spends, as the cloud
 * stores it (`cloud_credit_source`). One sentence each, because the choice decides whose money
 * every job spends from now on.
 */
const CREDIT_SOURCES: { key: "subscription" | "api" | "auto"; label: string; sentence: string }[] = [
  {
    key: "subscription",
    label: "Company subscription",
    sentence: "Every designer's job runs on the company Higgsfield subscription. While it is disconnected, jobs cannot start.",
  },
  {
    key: "api",
    label: "API key",
    sentence: "Every designer's job runs on the platform's Higgsfield API key, even while the subscription is connected.",
  },
  {
    key: "auto",
    label: "Automatic",
    sentence: "The company subscription while it is connected, and the API key while it is not.",
  },
];

/**
 * WS-HF shared login — the three caps the subscription path reads (`limits.*`). Part of the
 * page commit. Blank means "not set here": the cloud's default, or for the daily cap the
 * platform's own calls-per-day.
 */
const SUBSCRIPTION_LIMITS: {
  key: "subscription_calls_per_day" | "subscription_max_in_flight" | "max_credits_per_stage";
  testid: string;
  label: string;
  help: "subscriptionCallsPerDay" | "subscriptionInFlight" | "maxCreditsPerStage";
  unit: string;
}[] = [
  {
    key: "subscription_calls_per_day",
    testid: "limit-subscription-calls-per-day",
    label: "Subscription calls per day",
    help: "subscriptionCallsPerDay",
    unit: "calls per day",
  },
  {
    key: "subscription_max_in_flight",
    testid: "limit-subscription-max-in-flight",
    label: "Stages in flight at once",
    help: "subscriptionInFlight",
    unit: "stages",
  },
  {
    key: "max_credits_per_stage",
    testid: "limit-max-credits-per-stage",
    label: "Most credits per stage",
    help: "maxCreditsPerStage",
    unit: "credits",
  },
];

/** The cloud's defaults, shown as placeholders — `cloud/app/ai_relay/settings.py`. */
const SUBSCRIPTION_LIMIT_DEFAULTS: Record<string, string> = {
  subscription_calls_per_day: "none",
  subscription_max_in_flight: "4",
  max_credits_per_stage: "4",
};

const MODEL_DEFAULTS: Record<string, string> = {
  gemini: "gemini-2.5-flash",
  gpt: "gpt-4o",
};

/** Default IMAGE-generation model id per provider (placeholders for the
 *  image-model inputs). `resolve_image_model()` falls back to these when no
 *  override is stored. */
const IMAGE_MODEL_DEFAULTS: Record<string, string> = {
  gpt: "gpt-image-2",
  gemini: "imagen-3.0-generate-002",
  higgsfield: DEFAULT_HIGGSFIELD_MODEL,
};

/**
 * Providers that accept API keys (mock is excluded), IN THE ORDER THEY ARE DRAWN.
 *
 * WS-HF: `higgsfield` is first and is the platform default; `claude` is gone — it never had
 * image generation, and every generation this product runs is image-to-image.
 */
const REAL_PROVIDERS = ["higgsfield", "gemini", "gpt"];

/** Valid image size/quality values each provider accepts (dropdown sources). An
 *  empty list → free/default only. "custom" lets the operator type W×H manually. */
const PROVIDER_IMAGE_OPTIONS: Record<string, { sizes: string[]; qualities: string[] }> = {
  gpt: {
    sizes: ["1024x1024", "1536x1024", "1024x1536", "auto"],
    qualities: ["low", "medium", "high", "auto"],
  },
  gemini: {
    sizes: ["1024x1024", "1408x768", "768x1408", "1280x896", "896x1280"],
    qualities: [],
  },
};

/**
 * WS-HF — Higgsfield's resolution and quality are ONE setting for every path (the company
 * subscription, a desktop's own account, the API key), so they do not follow the API model.
 * Each path sends a value only to a model that takes it; the cloud drops the rest.
 */
function imageOptionsFor(provider: string): { sizes: string[]; qualities: string[] } {
  if (provider === "higgsfield") return { sizes: HF_RESOLUTIONS, qualities: HF_QUALITIES };
  return PROVIDER_IMAGE_OPTIONS[provider] ?? { sizes: [], qualities: [] };
}

/** What Higgsfield runs at when nothing is saved; other providers show "default". */
function unsavedFrame(provider: string): HfFrame | null {
  return provider === "higgsfield" ? DEFAULT_HF_FRAME : null;
}

/**
 * Higgsfield's resolutions are a CLOSED set the vendor validates; every other provider takes a
 * free W×H, which is why only they offer "custom…".
 */
function allowsCustomSize(provider: string): boolean {
  return provider !== "higgsfield";
}

/** Is a stored size value one of the provider's presets (vs a custom W×H)? */
function isPresetSize(provider: string, size: string): boolean {
  return !!size && imageOptionsFor(provider).sizes.includes(size);
}

/** The Higgsfield image model in force — the stored override, or the catalogue default. */
/**
 * The Higgsfield image model the select SHOWS. `image_models.higgsfield` is the model the cloud
 * relay runs on the platform's key, so only the API group's selectable ids can be shown as
 * chosen; a stored id outside that set — a device-only model, a text-to-image one, an id this
 * build does not know — shows the default. Display only, like F-5's quality segment: the
 * stored value is not rewritten until the operator picks one.
 */
function higgsfieldModelOf(imageModelInputs: Record<string, string>): string {
  const stored = imageModelInputs.higgsfield;
  return selectableModelsFor("cloud").some((m) => m.id === stored) ? stored : DEFAULT_HIGGSFIELD_MODEL;
}

/** `stored` when the executor can actually run it, else `fallback` — see `higgsfieldModelOf`. */
function selectableOr(wallet: CaseWallet, stored: string | undefined, fallback: string): string {
  return stored && selectableModelsFor(wallet).some((m) => m.id === stored) ? stored : fallback;
}

/** One wallet's pair, each stage the stored id when that wallet can run it, else the default. */
function seedPair(wallet: CaseWallet, stored?: Partial<Record<CaseJobStage, string>>): { clear: string; magenta: string } {
  return {
    clear: selectableOr(wallet, stored?.clear, CASE_JOB_MODEL_DEFAULTS[wallet].clear),
    magenta: selectableOr(wallet, stored?.magenta, CASE_JOB_MODEL_DEFAULTS[wallet].magenta),
  };
}

/** Fill every executor/stage slot from what is stored, falling back to the catalogue default. */
function seedCaseJobModels(stored?: CaseJobModels): Record<CaseWallet, { clear: string; magenta: string }> {
  // A stored id the executor cannot run is shown as the default, never as a selected option
  // the select cannot hold (which renders as a blank and saves as whatever the browser picks).
  return {
    cloud: seedPair("cloud", stored?.cloud),
    device: seedPair("device", stored?.device),
    subscription: seedPair("subscription", stored?.subscription),
  };
}

/** The limit inputs, from what is STORED — never the effective default, which a Save would
 *  then write back as an explicit value the operator never chose. */
function seedLimits(s: AiSettings): Record<string, string> {
  const out: Record<string, string> = {};
  for (const l of SUBSCRIPTION_LIMITS) {
    const v = s.limits?.[l.key];
    out[l.key] = v !== undefined && v !== null ? String(v) : "";
  }
  return out;
}

export function ProvidersTab() {
  const { client } = useServices();
  const isSuper = useSessionStore((s) => s.isSuperAdmin());
  const [settings, setSettings] = useState<AiSettings | null>(null);
  // The effect below writes whatever `settings` holds, so it is bound to the session it mounted in.
  const [epoch] = useState(cacheEpoch);
  const [usage, setUsage] = useState<AiUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // GEN-8: a refresh that fails OVER a cached copy is not an error (we still have
  // something to show) but it is NOT live either — say which, and how old.
  const [stale, setStale] = useState<Staleness | null>(null);
  const [saving, setSaving] = useState(false);

  // API key inputs (per provider)
  const [keyInputs, setKeyInputs] = useState<Record<string, string>>({});
  const [keySaving, setKeySaving] = useState<string | null>(null);

  // Vision/detect model inputs (per provider) — bound to settings.models
  const [modelInputs, setModelInputs] = useState<Record<string, string>>({});

  // Image-generation model inputs (per provider) — bound to settings.image_models
  const [imageModelInputs, setImageModelInputs] = useState<Record<string, string>>({});

  // Image params (size / quality) per provider
  const [imageInputs, setImageInputs] = useState<Record<string, { size: string; quality: string }>>({});

  // Limits
  const [callsPerDay, setCallsPerDay] = useState("");
  // WS-HF shared login — the subscription path's three caps, and the wallet switch.
  const [subLimits, setSubLimits] = useState<Record<string, string>>({});
  /** The company default product height; `null` = no height restriction. */
  const [heightDefault, setHeightDefault] = useState<number | null>(PRODUCT_HEIGHT_DEFAULT);
  const [creditSourceSaving, setCreditSourceSaving] = useState(false);

  // WS-HF — US dollars per Higgsfield credit, and the per-executor case-job stage models.
  const [caseModels, setCaseModels] = useState(() => seedCaseJobModels());
  // The customer-generation gate writes on its own, like the approvals gate beside it.
  const [customerGenSaving, setCustomerGenSaving] = useState(false);

  // Settings | Usage. Settings is the default so every existing probe selector on this route
  // still resolves at rest.
  const [subTab, setSubTab] = useState<"settings" | "usage">("settings");
  /* The open section, held here so a save or a re-fetch (which re-render the page) cannot move it. */
  const [openSection, setOpenSection] = useState<string | null>("provider");

  /* WS-Z5: ONE page commit for the five form sections that all wrote to `setAiSettings`
     with a different slice of the same object. Five saving flags became one. */
  const [pageSaving, setPageSaving] = useState(false);

  // Template-approval gate (platform-global)
  const [approvalsSaving, setApprovalsSaving] = useState(false);

  // Client-log retention (days)
  const [retentionDays, setRetentionDays] = useState("30");

  // Model-reachability check (re-pings the relay via getAiSettings round-trip).
  const [validateState, setValidateState] = useState<"idle" | "checking" | "ok" | "fail">("idle");

  function seedFromSettings(s: AiSettings) {
    setSettings(s);
    setModelInputs(s.models ? { ...s.models } : {});
    setImageModelInputs(s.image_models ? { ...s.image_models } : {});
    seedImageInputs(s);
    const cpd = s.limits?.calls_per_day;
    setCallsPerDay(cpd !== undefined ? String(cpd) : "");
    const rdays = s.client_log_retention_days;
    setRetentionDays(rdays != null ? String(rdays) : "30");
    // `?? DEFAULT` and not `|| DEFAULT`: a deliberate 0 is a price, not an absence.
    setCaseModels(seedCaseJobModels(s.case_job_models));
    setSubLimits(seedLimits(s));
    setHeightDefault(s.generation_product_height_pct === undefined ? PRODUCT_HEIGHT_DEFAULT : s.generation_product_height_pct);
  }

  async function refresh() {
    // Cache-first: show the last-known settings INSTANTLY (no spinner on every
    // open), then revalidate in the background. The cache is kept in sync on save
    // (effect below), so a fresh load only refreshes if something changed server-side.
    const cached = readCacheEntry<AiSettings>(AI_SETTINGS_CACHE);
    if (cached) {
      seedFromSettings(cached.data);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const [s, u] = await Promise.all([client.getAiSettings(), client.aiUsage("provider")]);
      seedFromSettings(s);
      setUsage(u);
      setStale(null);
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      // Never swallow it: with a cache we keep the panel populated but flag it stale,
      // without one it's a hard error. (Before GEN-8 the `if (!cached)` branch simply
      // dropped the reason on the floor and the tab looked perfectly live.)
      if (!cached) setError(reason);
      else setStale({ cachedAt: cached.at, reason });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isSuper) {
      setLoading(false);
      return;
    }
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, isSuper]);

  // Keep the cache in sync with the latest settings (load + every save), so the
  // next open is instant and only refreshes on an actual server-side change.
  useEffect(() => {
    if (settings) writeCache(AI_SETTINGS_CACHE, settings, epoch);
  }, [settings, epoch]);

  async function changeProvider(provider: string) {
    if (!provider || provider === settings?.active_provider) return;
    setSaving(true);
    setError(null);
    try {
      const next = await client.setAiSettings({ active_provider: provider });
      setSettings(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function saveKey(provider: string) {
    const key = keyInputs[provider];
    if (!key) return;
    await writeKey(provider, key);
  }

  /**
   * Remove a stored key. Save is (correctly) disabled with an empty input, so
   * without this there was NO way to take a leaked/rotated-out key off the
   * platform from the UI — the server clears on `keys: { provider: "" }`.
   * Destructive (every brokered call on that provider starts failing), so it
   * confirms like the other destructive admin actions.
   */
  async function clearKey(provider: string) {
    if (typeof window !== "undefined" && window.confirm &&
        !window.confirm(`Remove the stored ${enumLabel(provider)} API key? Brokered calls using ${enumLabel(provider)} will fail until a new key is saved.`)) {
      return;
    }
    await writeKey(provider, "");
  }

  async function writeKey(provider: string, key: string) {
    setKeySaving(provider);
    setError(null);
    try {
      const next = await client.setAiSettings({
        active_provider: settings?.active_provider ?? provider,
        keys: { [provider]: key },
      });
      setSettings(next);
      // Clear the input after successful save
      setKeyInputs((prev) => ({ ...prev, [provider]: "" }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setKeySaving(null);
    }
  }

  function seedImageInputs(s: AiSettings) {
    const seeded: Record<string, { size: string; quality: string }> = {};
    for (const p of REAL_PROVIDERS) {
      const v = s.image_settings?.[p];
      seeded[p] = { size: v?.size ?? "", quality: v?.quality ?? "" };
    }
    setImageInputs(seeded);
  }

  /**
   * The page commit (WS-Z5, Direction B rule 7).
   *
   * `saveModels`, `saveImageModels`, `saveImageSettings`, `saveLimits` and `saveRetention`
   * were five buttons calling `client.setAiSettings` with five slices of ONE settings
   * object. That is not five commits: the operator who edited a model id and a cap and
   * pressed the nearest Save wrote one of them and left the other on screen looking saved.
   * One patch, one round trip, one button — and every field the five of them wrote is in it.
   */
  async function savePage() {
    if (!settings) return;
    // P1 (kept): an EMPTIED cap field must CLEAR the stored cap, not leave it in place. The
    // server replaces the whole `limits` dict, so dropping the key is the clear.
    const limits: Record<string, number | string> = { ...settings.limits };
    if (callsPerDay !== "") limits.calls_per_day = Number(callsPerDay);
    else delete limits.calls_per_day;
    // The subscription's caps guard real money, so a typo is REFUSED, not dropped: the server
    // would answer 422, and a cap that silently stayed at its old value reads as saved.
    for (const l of SUBSCRIPTION_LIMITS) {
      const raw = (subLimits[l.key] ?? "").trim();
      if (raw === "") {
        delete limits[l.key];
        continue;
      }
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) {
        setError(`${l.label} must be 0 or more — nothing was saved.`);
        return;
      }
      limits[l.key] = n;
    }

    // Retention was silently dropped when out of range, which read as "saved" for a value
    // the server never received. It refuses the commit and says so instead.
    const days = Number(retentionDays);
    if (!Number.isFinite(days) || days < 1) {
      setError("Client-log retention must be at least 1 day — nothing was saved.");
      return;
    }

    const image_settings: Record<string, { size?: string; quality?: string }> = {};
    for (const p of REAL_PROVIDERS) {
      const v = imageInputs[p];
      // Always send each provider; the server treats blank fields as "clear/default".
      image_settings[p] = { size: v?.size ?? "", quality: v?.quality ?? "" };
    }

    setPageSaving(true);
    setError(null);
    try {
      const next = await client.setAiSettings({
        active_provider: settings.active_provider,
        models: modelInputs,
        image_models: imageModelInputs,
        image_settings,
        limits,
        client_log_retention_days: days,
        case_job_models: {
          cloud: { ...caseModels.cloud },
          device: { ...caseModels.device },
          subscription: { ...caseModels.subscription },
        },
        generation_product_height_pct: heightDefault,
      });
      setSettings(next);
      setModelInputs(next.models ? { ...next.models } : {});
      setImageModelInputs(next.image_models ? { ...next.image_models } : {});
      seedImageInputs(next);
      const cpd = next.limits?.calls_per_day;
      setCallsPerDay(cpd !== undefined ? String(cpd) : "");
      const rdays = next.client_log_retention_days;
      setRetentionDays(rdays != null ? String(rdays) : String(days));
      setCaseModels(seedCaseJobModels(next.case_job_models));
      setSubLimits(seedLimits(next));
      setHeightDefault(next.generation_product_height_pct === undefined ? PRODUCT_HEIGHT_DEFAULT : next.generation_product_height_pct);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPageSaving(false);
    }
  }

  /**
   * WS-HF shared login — which wallet every designer's cloud job spends. Written on its own,
   * like the customer gate: it is a platform switch, and folding it into the page commit would
   * leave a flipped choice unsaved behind a navigation with nothing on screen saying so.
   */
  async function saveCreditSource(choice: "subscription" | "api" | "auto") {
    if (!settings || choice === (settings.cloud_credit_source ?? "auto")) return;
    setCreditSourceSaving(true);
    setError(null);
    try {
      const next = await client.setAiSettings({
        active_provider: settings.active_provider,
        cloud_credit_source: choice,
      });
      setSettings(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreditSourceSaving(false);
    }
  }

  async function toggleApprovals(enabled: boolean) {
    if (!settings) return;
    setApprovalsSaving(true);
    setError(null);
    try {
      const next = await client.setAiSettings({
        active_provider: settings.active_provider,
        approvals_enabled: enabled,
      });
      setSettings(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setApprovalsSaving(false);
    }
  }

  /**
   * WS-HF — the customer-generation gate.
   *
   * Written on its own, like the approvals gate beside it: it is a PLATFORM switch, not a form
   * field, and folding it into the page commit would mean an operator who flipped it and then
   * navigated away left it unflipped with nothing on screen saying so.
   */
  async function toggleCustomerGeneration(enabled: boolean) {
    if (!settings) return;
    setCustomerGenSaving(true);
    setError(null);
    try {
      const next = await client.setAiSettings({
        active_provider: settings.active_provider,
        customer_generation_enabled: enabled,
      });
      setSettings(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCustomerGenSaving(false);
    }
  }

  /** Confirm the relay/models are reachable by round-tripping the settings
   *  endpoint (a real network probe — a 2xx means the cloud answered). */
  async function validateProvider() {
    setValidateState("checking");
    try {
      const s = await client.getAiSettings();
      // Only refresh the SERVER-OWNED view (key status, active provider, usage
      // badges). Do NOT call seedFromSettings() here: it re-seeds every form input
      // from the response, so a reachability check silently threw away whatever
      // model ids / limits / retention the operator had typed but not saved yet.
      setSettings(s);
      setValidateState("ok");
    } catch {
      setValidateState("fail");
    }
  }

  if (!isSuper) {
    /* WS-HF shared login: a tenant admin sees the company Higgsfield card READ-ONLY — which
       wallet their designers' images run on, and whether it is set up. Nothing else on this
       tab is theirs: provider keys and models are platform-global. */
    return (
      <section className="admin-tab" data-testid="admin-providers">
        <HiggsfieldCompanyReadOnly />
      </section>
    );
  }

  /* WS-HF — the SCREEN's order, not the server's. `available_providers` arrives in whatever
     order the cloud serialises it, and "Higgsfield first" is a product decision this tab owns.
     Anything the server lists that `REAL_PROVIDERS` does not know still renders, after the
     known ones, so a provider added server-side is never silently invisible here. */
  const providers = (() => {
    const all = settings?.available_providers ?? [];
    const known = REAL_PROVIDERS.filter((p) => all.includes(p));
    return [...known, ...all.filter((p) => !REAL_PROVIDERS.includes(p))];
  })();
  const hfModel = higgsfieldModelOf(imageModelInputs);

  return (
    <section className="admin-tab providers-compact" data-testid="admin-providers">
      <div className="admin-panel__head admin-panel__head--flush">
        <div>
          <strong className="admin-panel__title panel-title">AI providers <InfoHint text="Active provider, API keys, models, limits, and usage." /></strong>
        </div>
      </div>

      {error && <p role="alert">{error}</p>}
      <StaleNotice stale={stale} testid="providers-stale" />

      {loading ? (
        <p className="faint" data-testid="providers-loading">Loading provider settings...</p>
      ) : !settings ? (
        <EmptyState icon="settings" title="No provider settings" hint="Nothing configured yet." testId="providers-empty" />
      ) : (
        /* WS-HF — two sub-sections, one surface.
           Usage is not a second admin section: it is about the provider configured a scroll
           above it, and splitting them into two entries in the 200px sub-nav would have made
           "what did this cost" a place the operator has to remember rather than a tab beside
           the settings that caused it. `Tabs` is the product's one tab vocabulary (rule 5).
           `Settings` is the default, so every existing probe selector on this route — the
           approvals toggle, the key rows, the model tables — still resolves at rest. */
        <Tabs
          tabs={[
            { key: "settings", label: "Settings", testId: "providers-subtab-settings" },
            { key: "usage", label: "Usage", testId: "providers-subtab-usage" },
          ]}
          active={subTab}
          onChange={(k) => setSubTab(k as "settings" | "usage")}
          label="AI provider views"
          idPrefix="providers"
          panelClassName="providers-subpanel"
        >
        {subTab === "usage" ? (
          <UsagePanel usdPerCredit={DEFAULT_USD_PER_CREDIT} />
        ) : (
        <>
        {/* Owner, 2026-10-12: "I don't want columns, make it single column collapsible …
            everything is very cluttered". The five panels are one accordion: one column,
            one section open at a time, each head saying its state. A closed section's
            controls stay in the document, so every probe selector still resolves. */}
        <Accordion
          name="ai-providers"
          openKey={openSection}
          onOpenChange={setOpenSection}
          items={[
          {
            key: "provider",
            head: "Active provider",
            meta: <span className="faint" data-testid="providers-summary-provider">{enumLabel(settings.active_provider)}</span>,
            body: (
              <div className="admin-panel providers-section">
                <p className="admin-panel__sub">The provider the relay uses for every brokered generation.</p>
              <div className="setting-row">
                <div>
                  <span className="setting-row__label">Provider</span>{" "}<span data-testid="help-provider"><InfoHint label="About Provider" text={valueHelpText(PROVIDERS_HELP.provider)} /></span>
                </div>
                <div className="setting-row__control">
                  {/* Segmented toggle — active segment = the solid accent + white.
                   *  The functional <select> below stays in the DOM (visually hidden) so all
                   *  wiring + data-testid + change behaviour is preserved. */}
                  <div className="seg admin-seg" role="group" aria-label="Active provider">
                    {providers.filter((p) => p !== "mock").map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`seg__btn${p === settings.active_provider ? " seg__btn--on" : ""}`}
                        aria-pressed={p === settings.active_provider}
                        disabled={saving || !!settings.active_provider_env_locked}
                        onClick={() => changeProvider(p)}
                        data-testid={`provider-seg-${p}`}
                      >
                        {enumLabel(p)}
                      </button>
                    ))}
                  </div>
                  <select
                    id="ai-provider"
                    aria-label="Select provider"
                    className="admin-sr-only"
                    value={settings.active_provider}
                    onChange={(e) => changeProvider(e.target.value)}
                    disabled={saving}
                    data-testid="provider-select"
                  >
                    {providers.map((p) => (
                      <option key={p} value={p}>{enumLabel(p)}</option>
                    ))}
                  </select>
                  {saving && <span className="faint">Saving...</span>}
                </div>
              </div>
              {settings.active_provider_env_locked && (
                /* The server IGNORES a provider write while GENZ_AI_ACTIVE_PROVIDER is set.
                 * Before, the UI showed only the EFFECTIVE value, so the operator saw the env
                 * value as if it were their choice and a Save wrote it into the DB over the
                 * real one — invisibly, and unrecoverable by unsetting the variable. Show both
                 * values and say plainly that the toggle is inert. */
                /* WS-Z5 (R7): this was the file's one hint PARAGRAPH — `className="hint
                   hint--warn"` under a control, which is the channel rule 10 closes. It is
                   not an explanation, though, so it is not an `InfoHint`: it is a WARN
                   CONDITION about a control that has been made inert, and rule 9 keeps
                   exactly that tone. It becomes a 720px `.banner--warn`, which preserves
                   the live region, the `<strong>` values and the testid.
                   WS-Z5 fix round 1 (M-2): it lives UNDER the row it is about, as a child of
                   the card, not inside `.setting-row__control`. That cell is a narrow flex item
                   (the page was a two-column `.providers-grid` until 2026-10-12) and it does not shrink
                   a 720px object: the banner ran 628px past its card and 207px past the viewport
                   at 1280, clipped mid-word and painting over the "API keys" card beside it.
                   `document.scrollWidth` never changed, so no overflow gate saw it. As a child
                   of the card the 720px cap is a CAP — the card's content width is narrower. */
                <p className="banner banner--warn" role="status" data-testid="provider-env-locked">
                  {/* ONE child, so the sentence is one flex item (fix round 1, M-2). `.banner` is
                      `display: flex` for the `[icon][text]` shape; a `<p>` of mixed inline content
                      turns every `<code>`, `<strong>` and text run into its OWN flex item, which
                      is why this sentence laid out as a column of fragments running off the card
                      instead of wrapping. The wrapper costs nothing and makes it a paragraph. */}
                  <span>
                    Locked by the <code>GENZ_AI_ACTIVE_PROVIDER</code> environment variable
                    (<strong>{settings.active_provider_env_locked}</strong>), so this control does
                    nothing until that variable is removed.
                    {settings.active_provider_stored
                      && settings.active_provider_stored !== settings.active_provider_env_locked
                      ? <> Your saved choice is <strong>{settings.active_provider_stored}</strong>,
                        and it will take effect again once the override is removed.</>
                      : <> The saved choice underneath is
                        {" "}<strong>{settings.active_provider_stored ?? "unknown"}</strong>.</>}
                  </span>
                </p>
              )}
              <div className="setting-row">
                <div>
                  <span className="setting-row__label">Key status</span>{" "}<span data-testid="help-keyStatus"><InfoHint label="About Key status" text={valueHelpText(PROVIDERS_HELP.keyStatus)} /></span>
                </div>
                <div className="provider-chips">
                  {providers.filter((p) => p !== "mock").map((p) => (
                    <span
                      key={p}
                      className={`badge badge--plain ${settings.keys_configured?.[p] ? "badge--success" : "badge--neutral"}`}
                      data-testid={`key-status-${p}`}
                    >
                      {enumLabel(p)}
                    </span>
                  ))}
                </div>
              </div>
              </div>
            ),
          },
          {
            key: "keys",
            head: "API keys",
            meta: <span className="faint" data-testid="providers-summary-keys">{providers.filter((p) => p !== "mock" && settings.keys_configured?.[p]).length} of {providers.filter((p) => p !== "mock").length} set</span>,
            body: (
              <div className="admin-panel providers-section">
                <p className="admin-panel__sub">Stored server-side and never displayed — they never reach this device. <span data-testid="help-apiKey"><InfoHint label="About API keys" text={valueHelpText(PROVIDERS_HELP.apiKey)} /></span></p>
              <div data-testid="providers-keys">
                {REAL_PROVIDERS.map((p) => (
                  <div className="key-row" key={p} data-testid="provider-key-row">
                    <div className="key-row__meta">
                      <span className="setting-row__label">{enumLabel(p)}</span>
                      {settings.keys_configured?.[p] ? (
                        <span className="key-row__status key-row__status--ok">Configured</span>
                      ) : (
                        <span className="key-row__status">Not set</span>
                      )}
                    </div>
                    <div className="key-row__control">
                      <input
                        type="password"
                        className="key-row__input"
                        value={keyInputs[p] ?? ""}
                        onChange={(e) => setKeyInputs((prev) => ({ ...prev, [p]: e.target.value }))}
                        placeholder={settings.keys_configured?.[p] ? "••••••••" : "Enter API key…"}
                        data-testid={`key-input-${p}`}
                      />
                      <button
                        type="button"
                        className="btn btn--sm"
                        disabled={!keyInputs[p] || keySaving === p}
                        onClick={() => saveKey(p)}
                        data-testid={`key-save-${p}`}
                      >
                        {keySaving === p ? "Saving..." : "Save"}
                      </button>
                      {settings.keys_configured?.[p] && (
                        <button
                          type="button"
                          className="btn btn--sm btn--ghost"
                          disabled={keySaving === p}
                          onClick={() => void clearKey(p)}
                          data-testid={`key-clear-${p}`}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              </div>
            ),
          },
          {
            key: "models",
            head: "Models & image generation",
            meta: <span className="faint" data-testid="providers-summary-models">{validateState === "ok" ? "Reachable" : validateState === "fail" ? "Unreachable" : "Models, size and quality"}</span>,
            body: (
              <div className="admin-panel providers-section">
                <p className="admin-panel__sub">Vision (detect) model, the image model used for generation, and per-provider image params.</p>

              <div className="setting-row">
                <div>
                  <span className="setting-row__label">Validation</span>{" "}<span data-testid="help-validation"><InfoHint label="About Validation" text={valueHelpText(PROVIDERS_HELP.validation)} /></span>
                </div>
                <div className="validation-row">
                  {validateState === "fail" ? (
                    <span className="badge badge--danger" data-testid="provider-reachable">Unreachable</span>
                  ) : validateState === "checking" ? (
                    <span className="badge badge--neutral" data-testid="provider-reachable">Checking…</span>
                  ) : (
                    <span className="badge badge--success" data-testid="provider-reachable">Reachable</span>
                  )}
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={validateState === "checking"}
                    onClick={validateProvider}
                    data-testid="provider-validate"
                  >
                    {validateState === "checking" ? "Validating…" : "Validate"}
                  </button>
                </div>
              </div>

              <h3 className="admin-section-title">Vision model (detect) <span data-testid="help-visionModel"><InfoHint label="About the vision model" text={valueHelpText(PROVIDERS_HELP.visionModel)} /></span></h3>
              <div className="table-scroll">
              <table className="table" data-testid="providers-models">
                <thead><tr><th>Provider</th><th>Status</th><th>Model ID</th></tr></thead>
                <tbody>
                  {REAL_PROVIDERS.map((p) => (
                    <tr key={p} data-testid="provider-model">
                      <td>
                        {enumLabel(p)}
                        {p === settings.active_provider && (
                          <span className="badge badge--success badge--trailing">Active</span>
                        )}
                      </td>
                      <td>
                        {settings.keys_configured?.[p] ? (
                          <span className="badge badge--success" data-testid={`vision-key-status-${p}`}>Configured</span>
                        ) : (
                          <span className="badge badge--neutral" data-testid={`vision-key-status-${p}`}>Not set</span>
                        )}
                      </td>
                      <td>
                        <input
                          type="text"
                          value={modelInputs[p] ?? ""}
                          onChange={(e) => setModelInputs((prev) => ({ ...prev, [p]: e.target.value }))}
                          placeholder={MODEL_DEFAULTS[p] ?? "model-id"}
                          data-testid={`model-input-${p}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              <h3 className="admin-section-title">Image generation model <span data-testid="help-imageModel"><InfoHint label="About the image generation model" text={valueHelpText(PROVIDERS_HELP.imageModel)} /></span></h3>
              <div className="table-scroll">
              <table className="table" data-testid="providers-image-models">
                <thead><tr><th>Provider</th><th>Status</th><th>Image model ID</th></tr></thead>
                <tbody>
                  {REAL_PROVIDERS.map((p) => (
                    <tr key={p} data-testid="provider-image-model">
                      <td>
                        {enumLabel(p)}
                        {p === settings.active_provider && (
                          <span className="badge badge--success badge--trailing">Active</span>
                        )}
                      </td>
                      <td>
                        {settings.keys_configured?.[p] ? (
                          <span className="badge badge--success" data-testid={`image-key-status-${p}`}>Configured</span>
                        ) : (
                          <span className="badge badge--neutral" data-testid={`image-key-status-${p}`}>Not set</span>
                        )}
                      </td>
                      <td>
                        {/* WS-HF — Higgsfield's catalogue is a CLOSED set, so it is a `<select>`
                            and not a text box. An operator typing `nano-banana-2` into a free
                            field discovers the hyphen only when the vendor refuses the job. THIS
                            field is the model the cloud relay runs on the platform's key, so it
                            offers the API group ONLY (wave 3, fix 10): a device-only id here would
                            be sent to the REST API, which has no such model. The group is still
                            captioned, so it says whose money it is; the four text-to-image entries
                            are listed and disabled, because a model that ignores the reference
                            photos would bill for a case nobody asked for. The device wallet's
                            models are chosen per case stage in the Higgsfield card below. */}
                        {p === "higgsfield" ? (
                          <select
                            value={hfModel}
                            onChange={(e) => setImageModelInputs((prev) => ({ ...prev, higgsfield: e.target.value }))}
                            aria-label="Higgsfield image model"
                            data-testid={`image-model-input-${p}`}
                          >
                            <optgroup label={HIGGSFIELD_GROUP_LABELS.cloud}>
                              {modelsFor("cloud").map((m) => (
                                <option key={m.id} value={m.id} disabled={!m.referenceCapable}>
                                  {m.referenceCapable ? m.label : `${m.label} — ${TEXT_TO_IMAGE_NOTE}`}
                                </option>
                              ))}
                            </optgroup>
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={imageModelInputs[p] ?? ""}
                            onChange={(e) => setImageModelInputs((prev) => ({ ...prev, [p]: e.target.value }))}
                            placeholder={IMAGE_MODEL_DEFAULTS[p] ?? "image-model-id"}
                            data-testid={`image-model-input-${p}`}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              <h3 className="admin-section-title">Image size &amp; quality <span data-testid="help-imageSizeQuality"><InfoHint label="About image size and quality" text={valueHelpText(PROVIDERS_HELP.imageSizeQuality)} /></span></h3>
              <div className="table-scroll">
              <table className="table" data-testid="providers-image">
                <thead><tr><th>Provider</th><th>Size</th><th>Quality</th></tr></thead>
                <tbody>
                  {REAL_PROVIDERS.map((p) => (
                    <tr key={p} data-testid="provider-image-row">
                      <td>{enumLabel(p)}</td>
                      <td>
                        {(() => {
                          const opts = imageOptionsFor(p).sizes;
                          const cur = imageInputs[p]?.size ?? "";
                          const unsaved = unsavedFrame(p);
                          const isCustom = allowsCustomSize(p) && !!cur && !isPresetSize(p, cur);
                          const setSize = (size: string) =>
                            setImageInputs((prev) => ({ ...prev, [p]: { size, quality: prev[p]?.quality ?? "" } }));
                          const [cw, ch] = isCustom && cur.includes("x") ? cur.split("x") : ["", ""];
                          return (
                            <div className="row providers__size-pair">
                              <select
                                value={isCustom ? "custom" : unsaved && !opts.includes(cur) ? unsaved.resolution : cur}
                                onChange={(e) => setSize(e.target.value === "custom" ? "x" : e.target.value)}
                                data-testid={`image-size-${p}`}
                              >
                                {!unsaved && <option value="">default</option>}
                                {opts.map((s) => <option key={s} value={s}>{s}</option>)}
                                {/* Higgsfield validates a CLOSED set of resolutions, so there is
                                    no W×H to type. Offering "custom…" there would be offering a
                                    value the vendor always refuses. */}
                                {allowsCustomSize(p) && <option value="custom">custom…</option>}
                              </select>
                              {isCustom && (
                                <span className="row providers__size-pair">
                                  <input type="number" min={1} placeholder="W" value={cw}
                                    onChange={(e) => setSize(`${e.target.value}x${ch}`)}
                                    data-testid={`image-size-w-${p}`} className="input providers__dim" />
                                  <span className="faint">×</span>
                                  <input type="number" min={1} placeholder="H" value={ch}
                                    onChange={(e) => setSize(`${cw}x${e.target.value}`)}
                                    data-testid={`image-size-h-${p}`} className="input providers__dim" />
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        {(() => {
                          const qopts = imageOptionsFor(p).qualities;
                          const unsaved = unsavedFrame(p);
                          const setQ = (quality: string) =>
                            setImageInputs((prev) => ({ ...prev, [p]: { size: prev[p]?.size ?? "", quality } }));
                          if (qopts.length === 0) {
                            /* A model with no quality param gets no quality control — a `.seg`
                               whose every segment is inert reads as a broken single-select, and
                               the value it would send is one the vendor ignores. */
                            return <span className="faint" data-testid={`image-quality-none-${p}`}>—</span>;
                          }
                          const cur = imageInputs[p]?.quality ?? "";
                          /* F-5 — WHAT IS SHOWN, normalised against the segments that exist.
                             The stored quality need not be one of `qopts`: the server resolves
                             `GENZ_AI_{P}_IMAGE_QUALITY` OVER the DB value
                             (`cloud/app/ai_relay/settings.py::image_params`), so an operator who
                             set `standard` in the environment reads `standard` here — a value no
                             segment carries, which left NOTHING pressed and reintroduced the very
                             G-10s defect the `default` segment was added to fix.
                             DISPLAY ONLY: `imageInputs` is untouched, so a Save that does not
                             touch this control still sends the stored value. The screen may not
                             quietly rewrite what the operator set somewhere else — and the
                             `default` segment says in its tooltip what it is standing in for, so
                             the value is not hidden either. */
                          const shown = qopts.includes(cur) ? cur : unsaved?.quality ?? "";
                          /* Segmented SINGLE-SELECT — active segment = the solid accent.
                             `""` (send no quality, take the provider's own default) is a
                             SEGMENT, not the absence of one (F-7). It used to be neither: the
                             group rendered `low | medium | high | auto`, blank meant default,
                             and the only way back to blank was to click the segment that was
                             already on. Nothing on screen said so, and a `.seg` with nothing
                             pressed reads as a broken single-select — which is what
                             `07-admin-providers` reported the moment the measured gate could
                             see this tab (`0 buttons aria-pressed, expected 1`, six renderings).
                             The SIZE control in the same row has offered `default` in its
                             `<select>` since it was written. */
                          return (
                            <div className="seg admin-seg" role="group" aria-label={`${p} image quality`}>
                              {(unsaved ? qopts : ["", ...qopts]).map((q) => (
                                <button
                                  key={q || "default"}
                                  type="button"
                                  className={`seg__btn${shown === q ? " seg__btn--on" : ""}`}
                                  aria-pressed={shown === q}
                                  onClick={() => setQ(q)}
                                  data-testid={`image-quality-${p}-${q || "default"}`}
                                  title={
                                    q === "" && cur !== ""
                                      ? `The provider's own default. This provider is currently set to "${cur}", which is not one of the listed values — choosing default clears it.`
                                      : undefined
                                  }
                                >
                                  {q || "default"}
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              </div>
            ),
          },
          {
            key: "higgsfield",
            head: "Higgsfield account",
            meta: <InfoHint text="The account every Higgsfield generation bills against. Credits, invoices and plan changes are managed in Higgsfield's own console." />,
            body: (
              <div className="admin-panel providers-section" data-testid="providers-higgsfield">
                <p className="admin-panel__sub">The company sign-in, the wallet designers spend, its limits, and the model each case-job stage runs.</p>

              {/* WS-HF shared login — the owner signs the company in ONCE, here; every designer's
                  cloud job then runs on that subscription. */}
              <HiggsfieldCompanyConnection />

              <h3 className="admin-section-title">Designers&rsquo; jobs spend <span data-testid="help-wallet"><InfoHint label="About the wallet" text={valueHelpText(PROVIDERS_HELP.wallet)} /></span></h3>
              <div role="radiogroup" aria-label="Designers' jobs spend" data-testid="credit-source">
                {CREDIT_SOURCES.map((c) => (
                  /* The whole row is the label, so the target is the row and not a 13px dot. */
                  <label key={c.key} htmlFor={`credit-source-${c.key}`} className="setting-row credit-source__row" data-testid={`credit-source-row-${c.key}`}>
                    <span>
                      <span className="setting-row__label">{c.label}</span>
                      <span className="setting-row__hint">{c.sentence}</span>
                    </span>
                    <span className="setting-row__control">
                      <input
                        id={`credit-source-${c.key}`}
                        type="radio"
                        aria-label={c.label}
                        name="cloud-credit-source"
                        value={c.key}
                        checked={(settings.cloud_credit_source ?? "auto") === c.key}
                        disabled={creditSourceSaving}
                        onChange={() => void saveCreditSource(c.key)}
                        data-testid={`credit-source-${c.key}`}
                      />
                    </span>
                  </label>
                ))}
                {creditSourceSaving && <span className="faint" role="status">Saving…</span>}
              </div>

              <h3 className="admin-section-title">Subscription limits</h3>
              {SUBSCRIPTION_LIMITS.map((l) => (
                <div className="setting-row" key={l.key} data-testid={`${l.testid}-row`}>
                  <div>
                    <label className="setting-row__label" htmlFor={l.testid}>{l.label}</label>{" "}
                    <span data-testid={`help-${l.help}`}><InfoHint label={`About ${l.label}`} text={valueHelpText(PROVIDERS_HELP[l.help])} /></span>
                  </div>
                  <div className="setting-row__control">
                    <input
                      id={l.testid}
                      type="number"
                      aria-label={`${l.label} (${l.unit})`}
                      min={0}
                      step={l.key === "max_credits_per_stage" ? "0.5" : "1"}
                      value={subLimits[l.key] ?? ""}
                      placeholder={
                        l.key === "max_credits_per_stage" && settings.max_credits_per_stage != null
                          ? String(settings.max_credits_per_stage)
                          : SUBSCRIPTION_LIMIT_DEFAULTS[l.key]
                      }
                      onChange={(e) => setSubLimits((prev) => ({ ...prev, [l.key]: e.target.value }))}
                      data-testid={l.testid}
                    />
                    <span className="faint">{l.unit}</span>
                  </div>
                </div>
              ))}

              <h3 className="admin-section-title">Account</h3>
              <div className="setting-row">
                <div>
                  <span className="setting-row__label">Console</span>{" "}<span data-testid="help-console"><InfoHint label="About Console" text={valueHelpText(PROVIDERS_HELP.console)} /></span>
                </div>
                <div className="setting-row__control">
                  {/* `.btn--sm` carries the shared transparent 32px hit ring (G-26). A bare
                      anchor is a control to the gate and to a finger, and would have neither. */}
                  <a
                    className="btn btn--sm"
                    href={HIGGSFIELD_CONSOLE_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="higgsfield-console-link"
                  >
                    <Icon name="arrow-right" size={16} /> Higgsfield console
                  </a>
                </div>
              </div>

              <div className="setting-row">
                <div>
                  <span className="setting-row__label">$ per credit</span>{" "}<span data-testid="help-usdPerCredit"><InfoHint label="About dollars per credit" text={valueHelpText(PROVIDERS_HELP.usdPerCredit)} /></span>
                </div>
                <div className="setting-row__control">
                  {/* READ-ONLY. The server prices a credit from its environment
                      (`GENZ_AI_HIGGSFIELD_USD_PER_CREDIT`), not from these settings, so an input
                      here would be a price shown as saved that nothing ever reads. */}
                  <span className="faint" data-testid="usd-per-credit">
                    <code>GENZ_AI_HIGGSFIELD_USD_PER_CREDIT</code> (default ${DEFAULT_USD_PER_CREDIT.toFixed(2)})
                  </span>
                </div>
              </div>

              <div className="setting-row">
                <div>
                  <span className="setting-row__label">Customers can generate</span>{" "}<span data-testid="help-customersCanGenerate"><InfoHint label="About Customers can generate" text={valueHelpText(PROVIDERS_HELP.customersCanGenerate)} /></span>
                </div>
                <div className="setting-row__control">
                  <label className="admin-switch">
                    <input
                      type="checkbox"
                      aria-label="Customers can generate"
                      /* THE FIELD, as it is. `false` off, `true` on — and ABSENT is a cloud that
                         predates the gate, where customers generate, so it is shown ON: the same
                         reading `CustomizationCapabilities` gives the field (only an explicit
                         false closes it). Showing "off" for absent would tell the operator a
                         switch is closed that the server has never had. */
                      checked={settings.customer_generation_enabled ?? true}
                      disabled={customerGenSaving}
                      onChange={(e) => toggleCustomerGeneration(e.target.checked)}
                      data-testid="customer-generation-toggle"
                    />
                    <span className="admin-switch__track" aria-hidden="true" />
                  </label>
                  {customerGenSaving && <span className="faint">Saving…</span>}
                </div>
              </div>

              <h3 className="admin-section-title">Case-job product height <span data-testid="help-productHeight"><InfoHint label="About the product height" text={valueHelpText(PROVIDERS_HELP.productHeight)} /></span></h3>
              <ProductHeightControl value={heightDefault} onChange={setHeightDefault} testId="providers-height" />

              <h3 className="admin-section-title">Case-job stage models <span data-testid="help-stageModels"><InfoHint label="About the stage models" text={valueHelpText(PROVIDERS_HELP.stageModels)} /></span></h3>
              <div className="table-scroll">
                <table className="table" data-testid="providers-case-models">
                  <thead>
                    <tr>
                      <th>Wallet</th>
                      {CASE_STAGES.map((s) => <th key={s.key}>{s.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {CASE_EXECUTORS.map((ex) => (
                      <tr key={ex.key} data-testid="case-model-row">
                        <td>{ex.label}</td>
                        {CASE_STAGES.map((s) => (
                          <td key={s.key}>
                            <select
                              value={caseModels[ex.key][s.key]}
                              aria-label={`${ex.label} · ${s.label} model`}
                              onChange={(e) =>
                                setCaseModels((prev) => ({
                                  ...prev,
                                  [ex.key]: { ...prev[ex.key], [s.key]: e.target.value },
                                }))
                              }
                              data-testid={`case-model-${ex.key}-${s.key}`}
                            >
                              {selectableModelsFor(ex.key).map((m) => (
                                <option key={m.id} value={m.id}>{m.label}</option>
                              ))}
                            </select>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </div>
            ),
          },
          {
            key: "limits",
            head: "Limits & approvals",
            meta: <span className="faint" data-testid="providers-summary-limits">{settings.approvals_enabled ? "Approval required" : "No approval gate"}</span>,
            body: (
              <div className="admin-panel providers-section">
                <p className="admin-panel__sub">Platform-wide AI cap, the template-approval gate, and recorded usage.</p>
              <div className="setting-row">
                <div>
                  <span className="setting-row__label">Calls per day</span>{" "}<span data-testid="help-callsPerDay"><InfoHint label="About Calls per day" text={valueHelpText(PROVIDERS_HELP.callsPerDay)} /></span>
                </div>
                <div className="setting-row__control">
                  <input
                    id="calls-per-day"
                    type="number"
                    value={callsPerDay}
                    onChange={(e) => setCallsPerDay(e.target.value)}
                    placeholder="e.g. 1000"
                    data-testid="limits-calls-per-day"
                    min={0}
                  />
                </div>
              </div>
              <div className="setting-row">
                <div>
                  <span className="setting-row__label">Require template approval</span>{" "}<span data-testid="help-requireApproval"><InfoHint label="About Require template approval" text={valueHelpText(PROVIDERS_HELP.requireApproval)} /></span>
                </div>
                {/* P2: this row was rendered TWICE (two switches, same data-testid, same
                    state) — one of them is removed. The surviving one keeps the duplicate's
                    aria-label, since the <label> wraps no text of its own. */}
                <div className="setting-row__control">
                  <label className="admin-switch">
                    <input
                      type="checkbox"
                      aria-label="Require template approval"
                      checked={settings.approvals_enabled}
                      disabled={approvalsSaving}
                      onChange={(e) => toggleApprovals(e.target.checked)}
                      data-testid="approvals-toggle"
                    />
                    <span className="admin-switch__track" aria-hidden="true" />
                  </label>
                  {approvalsSaving && <span className="faint">Saving…</span>}
                </div>
              </div>

              <div className="setting-row">
                <div>
                  <span className="setting-row__label">Client-log retention</span>{" "}<span data-testid="help-logRetention"><InfoHint label="About Client-log retention" text={valueHelpText(PROVIDERS_HELP.logRetention)} /></span>
                </div>
                <div className="setting-row__control">
                  <input
                    id="retention-days"
                    type="number"
                    value={retentionDays}
                    onChange={(e) => setRetentionDays(e.target.value)}
                    placeholder="30"
                    data-testid="retention-days-input"
                    min={1}
                  />
                </div>
              </div>

              {/* WS-HF — "Recorded calls", not "Usage": the Usage SUB-TAB beside this one is the
                  credits-and-money page, and two things called Usage on one surface is a name
                  that stops identifying either. This is a count of brokered calls per provider,
                  which is what it says now. */}
              <h3 className="admin-section-title">Recorded calls</h3>
              {!usage || usage.rows.length === 0 ? (
                <p className="faint" data-testid="providers-usage-empty">No usage recorded.</p>
              ) : (
                <div className="table-scroll">
                <table className="table" data-testid="providers-usage">
                  <thead><tr><th>{usage.group_by === "tenant" ? "Tenant" : "Provider"}</th><th>Records</th></tr></thead>
                  <tbody>
                    {usage.rows.map((r, i) => (
                      <tr key={i} data-testid="provider-usage-row">
                        <td>{r.provider ?? r.tenant_id ?? "—"}</td>
                        <td>{r.records}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
              </div>
            ),
          },
          ]}
        />
          {/* Rule 7 — the page's commit, at the bottom of the working column, holding the
              one primary this surface is allowed.
              A SIBLING of the sections (fix round 1, M-3). Inside the old `.providers-grid` — a
              `repeat(auto-fill, minmax(360px, 1fr))` grid — the bar was a grid CELL: 373px of
              a 762px grid at 1280, a half-width strip in the left column that floated over the
              "Models & image generation" card while scrolled. `.admin-tab` is the column, so
              out here the bar spans it. */}
          <ActionBar
            label="AI provider actions"
            primary={
              <button
                type="button"
                className="btn btn--primary"
                disabled={pageSaving}
                onClick={savePage}
                data-testid="providers-save"
              >
                {pageSaving ? "Saving…" : "Save changes"}
              </button>
            }
          />
        </>
        )}
        </Tabs>
      )}
    </section>
  );
}
